import React, { useCallback, useMemo, useState, FC } from 'react';
import Image from 'react-bootstrap/Image';
import { format } from 'date-fns';

import type { IAction } from '../../../../types/affiliates.types';
import type { IProductCardProps, IProductLogo } from '../../productCard';
import BindingButton from '../../../BindingButton';
import AccountRecoveryButton from '../../../AccountRecoveryButton';

const WbdProductCard: FC<IProductCardProps> = ({ data, logos, products, onActivateButtonClick, onGetAccountRecovery }) => {
  const {
    cps,
    cpDescription,
    error,
    logId,
    linked,
    metadata,
    subjectId,
    binding
  } = data;
  const [isLoading, setIsLoading] = useState(false);

  const suspendAction = useMemo(() => binding?.actions?.find((action: IAction) => action?.action === 'SUSPEND'), [binding]);

  const serviceName = useMemo(() => cpDescription || metadata?.name || cps, [cpDescription, metadata?.name, cps]);

  const activatedProducts = useMemo(() => [...products]
    .sort((a, b) => (a.binding?.createdAt || '').localeCompare(b.binding?.createdAt || ''))
    .map(product => {
      const suspend = product.binding?.actions?.find((action: IAction) => action?.action === 'SUSPEND');
      return {
        productID: product.productID,
        name: product.metadata?.description?.[0] || product.cpDescription,
        bound: product.binding?.status === 'BOUND',
        suspended: product.binding?.status === 'SUSPENDED',
        since: product.binding?.createdAt ? format(new Date(product.binding.createdAt), 'dd/MM/yyyy') : '',
        suspendedSince: suspend?.updatedAt ? format(new Date(suspend.updatedAt), 'dd/MM/yyyy') : ''
      };
    }), [products]);

  const hasSuspended = useMemo(() => activatedProducts.some(product => product.suspended), [activatedProducts]);

  const hasBound = useMemo(() => activatedProducts.some(product => product.bound), [activatedProducts]);

  // Un producto pendiente todavía necesita el botón Activar, aunque otro esté suspendido.
  const hasPending = useMemo(() => activatedProducts.some(product => !product.bound && !product.suspended), [activatedProducts]);

  const productNames = useMemo(() => {
    const names = activatedProducts.filter(product => product.bound).map(product => product.name);
    return names.length > 1 ? `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}` : names[0] || serviceName;
  }, [activatedProducts, serviceName]);

  const loginUrl = useMemo(() => data?.loginUrl || '', [data?.loginUrl]);

  const displayLogos = useMemo<IProductLogo[]>(() => {
    const resolved = logos?.length ? logos : [{ src: metadata?.image, alt: serviceName }];
    return resolved.filter(logo => Boolean(logo.src));
  }, [logos, metadata?.image, serviceName]);

  const redirectURLRegisteredUser = useMemo(() => data?.customData?.find(p => p.key === 'redirectURLRegisteredUser')?.value, [data?.customData]);

  const errorMessage = useMemo(() => {
    if (!error) return '';
    const baseMessage = '* Se ha producido un error en la activación de su producto.';
    if (logId) return `${baseMessage} Código de error: ${error}. Identificador del error: ${logId}`;
    return `${baseMessage} Identificador del error: ${error}`;
  }, [error, logId]);

  const activateButtonClickHandler = useCallback((e: any) => {
    e.preventDefault();

    if (linked) {
      const loginTarget = redirectURLRegisteredUser || loginUrl;
      if (loginTarget) {
        window.open(loginTarget);
      }
      return;
    }

    setIsLoading(true);
    if (onActivateButtonClick) {
      Promise.resolve(onActivateButtonClick())
        .catch((err) => console.error('Error al activar producto:', err))
        .finally(() => setTimeout(() => setIsLoading(false), 1000));
    }
  }, [linked, onActivateButtonClick, redirectURLRegisteredUser, loginUrl]);

  if (products.length === 1 && binding.status === 'SUSPENDED' && suspendAction) {
    return (
      <div className='suspended'>
        {metadata?.image && (
          <div className='suspended-logo'>
            <Image src={metadata.image} alt={serviceName} />
          </div>
        )}
        <p>Tu servicio de <strong>{serviceName}</strong> se encuentra suspendido por deuda pendiente, desde el día {format(suspendAction.updatedAt, 'dd/MM/yyyy')}</p>
        <p>Paga tus cuentas de forma segura <a href='https://vtr.com/portaldepagos/initial'>aquí</a></p>
      </div>
    );
  }

  return (
    <div className={`product-item${error ? ' product-item--error' : ''}`}>
      <div className={`${cps} ${linked ? 'bound' : ''}`}>
        <div className={`content${linked ? ' content--activated' : ''}`}>
          <div className={`logo${displayLogos.length > 1 ? ' logo--multiple' : ''}`}>
            {displayLogos.map(logo => (
              <Image key={logo.src} src={logo.src} alt={logo.alt} />
            ))}
          </div>
          {linked || (hasSuspended && !hasPending) ? (
            <div className='info'>
              <div className="activated">
                {hasBound && (
                  <>
                    <p className="activated__title">
                      <svg className="activated__check" viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                        <circle cx="10" cy="10" r="10" fill="#00A94F" />
                        <path d="M6 10.5l2.5 2.5L14 7.5" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Ya activaste {productNames}
                    </p>
                    <p className="activated__subtitle">Tu beneficio de VTR está activo.</p>
                  </>
                )}
                <ul className="activated__list">
                  {activatedProducts.map(product => (
                    <li key={product.productID}>
                      <strong>{product.name}</strong>
                      {product.suspended
                        ? <>: Tu servicio se encuentra suspendido por deuda pendiente{product.suspendedSince && `, desde el día ${product.suspendedSince}`}</>
                        : product.since && <>: disfrútalo desde el <span>{product.since}</span></>}.
                    </li>
                  ))}
                </ul>
                {hasSuspended && (
                  <p className="activated__subtitle">Paga tus cuentas de forma segura <a href='https://vtr.com/portaldepagos/initial'>aquí</a></p>
                )}
              </div>
            </div>
          ) : (
            <div className='info'>
              <BindingButton
                disabled={!!error}
                isLoading={isLoading}
                onClickHandler={activateButtonClickHandler}
                title="Activar"
              />
            </div>
          )}
        </div>
        {linked && (
          <div className="activated-ready">
            <p className="activated-ready__title">¡Ya está todo listo!</p>
            <p>Vuelve a ingresar a la app de {serviceName} con la cuenta que ya creaste y disfruta de {productNames}.</p>
          </div>
        )}
        {subjectId && <div className="footer">Tu ID de suscripción es: <span>{subjectId}</span></div>}
        <AccountRecoveryButton cps={cps} linked={linked} onGetAccountRecovery={onGetAccountRecovery} />
      </div>
      {error && <div className="error-susbscription">{errorMessage}</div>}
    </div>
  );
};

export default WbdProductCard;
